import { memo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { cn, imageProps, lqipStyle } from '../../lib/format.js';
import { PriceTag } from './PriceTag.jsx';
import { Rating } from './Rating.jsx';
import { Badge } from '../ui/Badge.jsx';
import { Button } from '../ui/Button.jsx';

/**
 * The product card.
 *
 * A framed tile: the shot on cream, the sizes it comes in, the name, the
 * rating, the price, and one action. Every card in a row carries every line —
 * an unreviewed product still shows its empty stars — and the price and action
 * sit at the foot, so a grid of mixed-length names still lines up.
 *
 * The title is the card's one link, stretched over the whole tile; the action
 * button sits above it. A tile with two links to the same page makes a screen
 * reader announce every product twice.
 *
 * The image is a plain <img> with srcset, explicit dimensions, and lazy loading.
 * Wrapping catalogue imagery in a fetch-to-blob would forfeit HTTP caching,
 * streaming decode, and priority hints, and would make the store measurably
 * slower.
 */

const MAX_CHIPS = 4;

/** Units that read as one token: "50ml", not "50 Ml". */
const MEASURE = /^\d+(?:\.\d+)?\s*(?:ml|l|g|kg|oz|mm|cm)$/i;

const formatOption = (value) => {
  const text = String(value).trim();
  if (MEASURE.test(text)) return text.replace(/\s+/g, '').toLowerCase();
  const spaced = text.replace(/[-_]+/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

/** The first variation axis's values — volume for a fragrance, size for kit. */
const optionChips = (product) => {
  if (product.type !== 'variable') return [];
  const axis = product.variationAttributes?.find((entry) => entry?.values?.length > 0);
  return (axis?.values ?? []).map(formatOption);
};

const ProductCardComponent = ({ product, priority = false, className, onQuickAdd }) => {
  const imageRef = useRef(null);

  const primary = product.images?.[0];
  const secondary = product.images?.[1];

  const isVariable = product.type === 'variable';
  const price = isVariable ? (product.priceRange?.min ?? 0) : product.price;
  const compareAt = isVariable ? 0 : product.compareAtPrice;

  const soldOut = product.trackInventory !== false && (isVariable ? product.totalStock : product.stock) <= 0;

  const href = `/product/${product.slug}`;
  const chips = optionChips(product);
  const shownChips = chips.slice(0, MAX_CHIPS);
  const sizes = '(min-width: 1280px) 20vw, (min-width: 768px) 33vw, 50vw';

  return (
    <article
      className={cn(
        'group relative flex h-full flex-col border border-mist bg-surface p-3 transition-shadow duration-300',
        'hover:shadow-[0_10px_30px_-18px_rgba(13,13,13,0.35)]',
        className,
      )}
      data-world={product.world}
    >
      <div className="relative aspect-3/4 w-full overflow-hidden bg-cream" style={lqipStyle(primary)} ref={imageRef}>
        {primary && (
          <img
            {...imageProps(primary, { sizes, eager: priority })}
            alt={primary.alt || product.title}
            className={cn(
              'absolute inset-0 size-full object-cover transition-[opacity,transform] duration-700 ease-out',
              'motion-safe:group-hover:scale-[1.04]',
              secondary && 'motion-safe:group-hover:opacity-0',
            )}
          />
        )}

        {secondary && (
          <img
            {...imageProps(secondary, { sizes })}
            alt=""
            aria-hidden="true"
            className="absolute inset-0 size-full object-cover opacity-0 transition-[opacity,transform] duration-700 ease-out motion-safe:group-hover:scale-[1.04] motion-safe:group-hover:opacity-100"
          />
        )}

        <div className="pointer-events-none absolute top-2 left-2 flex flex-col items-start gap-1.5">
          {!soldOut && product.isNewArrival && <Badge tone="accent">New</Badge>}
          {!soldOut && !product.isNewArrival && compareAt > price && <Badge tone="success">Reduced</Badge>}
        </div>
      </div>

      <div className="flex flex-1 flex-col items-center pt-3 text-center">
        {shownChips.length > 0 && (
          <ul className="mb-2.5 flex flex-wrap justify-center gap-1.5" aria-label="Available in">
            {shownChips.map((label) => (
              <li
                key={label}
                className="min-w-10 border border-mist px-2 py-1 text-2xs leading-none font-medium text-ink"
              >
                {label}
              </li>
            ))}
            {chips.length > MAX_CHIPS && (
              <li className="px-1 py-1 text-2xs leading-none text-muted">+{chips.length - MAX_CHIPS}</li>
            )}
          </ul>
        )}

        <h3 className="text-xs leading-snug font-medium text-ink">
          <Link
            to={href}
            aria-label={product.brand?.name ? `${product.title}, ${product.brand.name}` : undefined}
            className={cn(
              'line-clamp-3 transition-colors group-hover:text-brand',
              // The stretched link: its box covers the whole card.
              'after:absolute after:inset-0',
              'focus-visible:outline-none focus-visible:after:outline-2 focus-visible:after:outline-offset-2 focus-visible:after:outline-brand',
            )}
          >
            {product.title}
          </Link>
        </h3>

        <Rating value={product.ratingAvg ?? 0} count={product.ratingCount ?? 0} compact className="mt-2" />

        <div className="mt-auto flex w-full flex-col items-center pt-3">
          <PriceTag
            price={price}
            compareAtPrice={compareAt}
            priceRange={product.priceRange}
            isVariable={isVariable}
            showSaving={false}
            size="sm"
            className="justify-center"
          />

          <div className="relative z-10 mt-3 flex h-8 items-center">
            {soldOut ? (
              <p className="text-2xs font-semibold tracking-[0.08em] text-muted uppercase">Sold out</p>
            ) : isVariable ? (
              <Button to={href} size="xs" tabIndex={-1} aria-hidden="true">
                Select options
              </Button>
            ) : onQuickAdd ? (
              <Button size="xs" onClick={() => onQuickAdd(product, imageRef.current)} aria-label={`Add ${product.title} to cart`}>
                Add to cart
              </Button>
            ) : (
              <Button to={href} size="xs" tabIndex={-1} aria-hidden="true">
                View product
              </Button>
            )}
          </div>
        </div>
      </div>
    </article>
  );
};

/**
 * A shop page renders up to a hundred of these and re-renders on every filter
 * change. Memoising on the product id keeps that to the cards that changed.
 */
export const ProductCard = memo(
  ProductCardComponent,
  (previous, next) =>
    previous.product._id === next.product._id &&
    previous.product.totalStock === next.product.totalStock &&
    previous.priority === next.priority &&
    Boolean(previous.onQuickAdd) === Boolean(next.onQuickAdd),
);

export default ProductCard;
